import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useFieldAttributes } from './field-accessibility';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — form controls. All text-entry controls share one touch-safe
 * contract: 44px default height, readable mobile text, sober focus ring and no
 * per-screen handmade padding.
 */
const fieldControlVariants = cva(
  'w-full rounded-so-md border border-sober-border bg-sober-surface text-sober-text placeholder:text-sober-secondary transition-[border-color,box-shadow] duration-150 focus:border-sober-primary-fg focus:ring-2 focus:ring-sober-primary-fg focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      controlSize: {
        md: 'h-11 px-3.5 text-[16px] sm:text-so-14',
        sm: 'h-10 px-3 text-[16px] sm:text-so-14',
        dense: 'h-9 px-3 text-so-14',
      },
    },
    defaultVariants: { controlSize: 'md' },
  }
);

export type FieldControlSize = NonNullable<
  VariantProps<typeof fieldControlVariants>['controlSize']
>;

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof fieldControlVariants> {}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', controlSize, ...props }, ref) => {
    const accessible = useFieldAttributes(props);
    return (
    <input
      ref={ref}
      type={type}
      className={cn(fieldControlVariants({ controlSize }), className)}
      {...props}
      {...accessible}
    />
    );
  }
);
Input.displayName = 'Input';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof fieldControlVariants> {}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, controlSize = 'md', ...props }, ref) => {
    const accessible = useFieldAttributes(props);
    return (
    <textarea
      ref={ref}
      className={cn(
        fieldControlVariants({ controlSize }),
        'h-auto resize-y py-3 leading-relaxed',
        controlSize === 'dense' ? 'min-h-[84px]' : 'min-h-[112px]',
        className
      )}
      {...props}
      {...accessible}
    />
    );
  }
);
Textarea.displayName = 'Textarea';

export interface NativeSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'>,
    VariantProps<typeof fieldControlVariants> {}

export const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(
  ({ className, controlSize, children, ...props }, ref) => {
    const accessible = useFieldAttributes(props);
    return (
    <select
      ref={ref}
      className={cn(fieldControlVariants({ controlSize }), 'appearance-none pr-9', className)}
      {...props}
      {...accessible}
    >
      {children}
    </select>
    );
  }
);
NativeSelect.displayName = 'NativeSelect';

export interface NumberInputProps extends Omit<InputProps, 'type' | 'inputMode'> {}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>((props, ref) => (
  <Input ref={ref} type="number" inputMode="decimal" {...props} />
));
NumberInput.displayName = 'NumberInput';

export interface MoneyInputProps extends Omit<InputProps, 'type' | 'inputMode'> {}

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>((props, ref) => (
  <Input ref={ref} type="text" inputMode="decimal" {...props} />
));
MoneyInput.displayName = 'MoneyInput';

// ─────────────────────────────────────────────────────────────────────────────
// RC-016 (T6): NumberField — campo numérico controlado SEM clamp no keystroke.
// O anti-padrão `Math.max(1, Number(v) || 1)` no onChange tornava o valor
// insubstituível (backspace devolvia 1 no mesmo keystroke; digitar concatenava).
// Padrão promovido do DelayInput: draft string aceita '' e 0 TRANSITÓRIOS;
// commit/reconcile no blur; min/max aplicados SÓ a valor válido não-vazio —
// vazio permanece null (a validação de submit do consumidor bloqueia com toast).
// ─────────────────────────────────────────────────────────────────────────────
export interface NumberFieldProps
  extends Omit<InputProps, 'type' | 'inputMode' | 'value' | 'onChange'> {
  value: number | null;
  onValueChange: (n: number | null) => void;
  min?: number;
  max?: number;
  /** 'int' (default) só dígitos; 'decimal' preserva separador transitório ("100,"). */
  mode?: 'int' | 'decimal';
}

export const NumberField = React.forwardRef<HTMLInputElement, NumberFieldProps>(
  ({ value, onValueChange, min, max, mode = 'int', onBlur, ...props }, ref) => {
    const [draft, setDraft] = React.useState<string | null>(null);

    const clamp = (n: number) => {
      let out = n;
      if (min !== undefined && out < min) out = min;
      if (max !== undefined && out > max) out = max;
      return out;
    };

    const parse = (raw: string): { text: string; num: number | null } => {
      if (mode === 'decimal') {
        const text = raw.replace(/[^\d.,]/g, '');
        // CodeRabbit: com vírgula presente, pontos são MILHAR ("1.234,56"); sem vírgula,
        // um único ponto é decimal.
        const normalized = text.includes(',')
          ? text.replace(/\./g, '').replace(',', '.')
          : text;
        const num = text === '' || /[.,]$/.test(text) ? null : Number(normalized);
        return { text, num: Number.isFinite(num as number) ? (num as number) : null };
      }
      const text = raw.replace(/\D/g, '');
      return { text, num: text === '' ? null : Number(text) };
    };

    return (
      <Input
        ref={ref}
        inputMode={mode === 'decimal' ? 'decimal' : 'numeric'}
        value={draft ?? (value === null ? '' : String(value))}
        onChange={(e) => {
          const { text, num } = parse(e.target.value);
          setDraft(text);
          // Estado transitório ('', '100,') vive no draft; número válido sobe SEM clamp
          // (clamp no keystroke impedia digitar "12" com min=1 — o "1" travava).
          onValueChange(num);
        }}
        onBlur={(e) => {
          // Reconciliação no blur: min/max SÓ em valor válido; vazio permanece null.
          if (value !== null) {
            const settled = clamp(value);
            if (settled !== value) onValueChange(settled);
          }
          setDraft(null);
          onBlur?.(e);
        }}
        {...props}
      />
    );
  },
);
NumberField.displayName = 'NumberField';

export { fieldControlVariants, fieldControlVariants as inputVariants };
